import type {
  EventSourceView,
  ReadNotificationsRequest,
  SourcedEventView,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { readDevicePreference, writeDevicePreference } from '@/lib/device-preferences';

/**
 * Where a notification was stored: this server (`''`), or the linked Crowdex Cloud, whose
 * notifications a machine's server lists and streams as its own.
 */
type NotificationSource = EventSourceView;

export type FeedNotification = SourcedEventView;

/**
 * How often notifications are fetched anyway: the stream says when one is stored, this
 * catches what a dropped stream missed and what was read elsewhere.
 */
const POLL_MS = 30_000;
/** A device that never showed any shows the unread ones of the last day, and no older. */
const UNREAD_WINDOW_MS = 24 * 60 * 60 * 1_000;
/**
 * The newest notifications this device already showed, per server: their clocks differ, and the
 * cloud's arrive late (relayed, replayed after a drop).
 */
const shownKey = (source: NotificationSource) =>
  `crowdex.notifications.shown${source === '' ? '' : `.${source.slice(1)}`}`;
const SOURCES: readonly NotificationSource[] = ['', '/cloud'];

/** The user's notifications of every server, as this one lists them. */
function loadNotifications(): Promise<FeedNotification[]> {
  return apiFetch('/api/notifications', { cache: 'no-store' });
}

/** The conversations with unread notifications on every server, past the list's latest. */
function loadUnreadThreadIds(): Promise<string[]> {
  return apiFetch('/api/notifications/unread-threads', { cache: 'no-store' });
}

/** Marks them read wherever they were stored. */
export async function markNotificationsRead(request: ReadNotificationsRequest): Promise<void> {
  await apiFetch('/api/notifications', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });
}

type StreamedListener = (event: FeedNotification) => void;
const streamedListeners = new Set<StreamedListener>();

/** Hears every event as the server streams it (notifications or not), while the feed runs. */
export function onStreamedEvent(listener: StreamedListener): () => void {
  streamedListeners.add(listener);
  return () => streamedListeners.delete(listener);
}

/**
 * What this device already showed: its newest notifications' time, and their ids
 * (another one may share that millisecond and arrive later).
 */
type ShownCursor = { at: string; ids: string[] };

function shownCursor(stored: string | null): ShownCursor {
  try {
    const parsed = JSON.parse(stored ?? '') as Partial<ShownCursor>;
    if (typeof parsed.at === 'string' && Array.isArray(parsed.ids))
      return { at: parsed.at, ids: parsed.ids.filter((id) => typeof id === 'string') };
  } catch {
    // Nothing shown yet on this device.
  }
  return { at: '', ids: [] };
}

function isAfter(notification: Pick<FeedNotification, 'id' | 'createdAt'>, shown: ShownCursor) {
  return (
    notification.createdAt > shown.at ||
    (notification.createdAt === shown.at && !shown.ids.includes(notification.id))
  );
}

/** The cursor once these are shown; the same object when nothing newer came. */
function advancedCursor(
  shown: ShownCursor,
  notifications: readonly Pick<FeedNotification, 'id' | 'createdAt'>[],
): ShownCursor {
  const newest = notifications.reduce(
    // biome-ignore lint/complexity/useMathMinMax: ISO timestamps are strings, Math.max would give NaN.
    (at, entry) => (entry.createdAt > at ? entry.createdAt : at),
    shown.at,
  );
  const ids = notifications.filter((entry) => entry.createdAt === newest).map((entry) => entry.id);
  if (newest === shown.at && ids.every((id) => shown.ids.includes(id))) return shown;
  return { at: newest, ids: newest === shown.at ? [...new Set([...shown.ids, ...ids])] : ids };
}

export type NotificationFeed = {
  /** The account's latest notifications, newest first. */
  current(): readonly FeedNotification[];
  /** The conversations with unread notifications, listed or older. */
  unreadThreadIds(): ReadonlySet<string>;
  subscribe(listener: () => void): () => void;
  /** Fetches now instead of at the next poll; settles once the list is fetched. */
  refresh(): Promise<void>;
  /**
   * Marks these read (on their servers too), a conversation's or all; never what waits on the
   * user (`waitingTypes`): reading is not answering.
   */
  markRead(target: readonly FeedNotification[] | { threadId: string } | 'all'): void;
  /** Shows these no longer waiting at once: answered, their server already settled them. */
  settle(target: readonly FeedNotification[]): void;
  /** The types whose events wait on the user, which only settle (`markRead` leaves them). */
  setWaitingTypes(types: ReadonlySet<string>): void;
  /** The last fetch failed (offline, the server down) and none succeeded since. */
  failed(): boolean;
  stop(): void;
};

/** Whether the user reads it as news: anything but what waits on them. */
export function readable(
  notification: Pick<FeedNotification, 'type'>,
  waitingTypes: ReadonlySet<string>,
): boolean {
  return !waitingTypes.has(notification.type);
}

/**
 * Keeps the account's latest notifications, as this server lists and streams them (a machine's
 * with its linked cloud's), and hands each new unread one to `onFresh` once per device: what is
 * shown is remembered, what is read is the servers' (the user opened it, its conversation, or
 * marked all read).
 */
export function createNotificationFeed(
  onFresh: (notification: FeedNotification) => void,
): NotificationFeed {
  let list: readonly FeedNotification[] = [];
  let unread: ReadonlySet<string> = new Set();
  let waitingTypes: ReadonlySet<string> = new Set();
  let lastFailed = false;
  const listeners = new Set<() => void>();
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  let again = false;
  // Who waits on the next poll to start (one running has fetched too early).
  const waiting: (() => void)[] = [];
  const settle = (served: readonly (() => void)[]) => {
    for (const resolve of served) resolve();
  };

  const publish = (next: readonly FeedNotification[], nextUnread = unread) => {
    list = next;
    unread = nextUnread;
    for (const listener of listeners) listener();
  };

  const poll = async () => {
    // One poll at a time; one asked for meanwhile runs right after it.
    if (stopped) {
      settle(waiting.splice(0));
      return;
    }
    if (running) {
      again = true;
      return;
    }
    running = true;
    const served = waiting.splice(0);
    clearTimeout(timer);
    try {
      const [fetched, threads] = await Promise.all([loadNotifications(), loadUnreadThreadIds()]);
      if (stopped) return;
      lastFailed = false;
      publish(fetched, new Set(threads));
      const floor = Date.now() - UNREAD_WINDOW_MS;
      const fresh: FeedNotification[] = [];
      for (const source of SOURCES) {
        const ofSource = fetched.filter((notification) => notification.source === source);
        const shown = shownCursor(readDevicePreference(shownKey(source)));
        fresh.push(
          ...ofSource.filter(
            (notification) =>
              notification.readAt === null &&
              Date.parse(notification.createdAt) > floor &&
              isAfter(notification, shown),
          ),
        );
        const next = advancedCursor(shown, ofSource);
        if (next !== shown)
          void writeDevicePreference(shownKey(source), JSON.stringify(next)).catch(() => undefined);
      }
      // Oldest first, as they happened.
      fresh.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
      for (const notification of fresh) onFresh(notification);
    } catch {
      // Offline or signed out: said (Retry), and the next poll tries again.
      if (!stopped && !lastFailed) {
        lastFailed = true;
        publish(list);
      }
    } finally {
      running = false;
      settle(served);
      if (again) {
        again = false;
        void poll();
      } else if (!stopped) timer = setTimeout(() => void poll(), POLL_MS);
    }
  };
  const refresh = () =>
    new Promise<void>((resolve) => {
      waiting.push(resolve);
      void poll();
    });
  const onFocus = () => void refresh();

  /** Shows these read here; a conversation stays unread while news of it is (the poll says). */
  const showRead = (
    read: readonly FeedNotification[],
    target: Parameters<NotificationFeed['markRead']>[0],
  ) => {
    const now = new Date().toISOString();
    const readIds = new Set(
      read.map((notification) => `${notification.source}:${notification.id}`),
    );
    const next = list.map((notification) =>
      readIds.has(`${notification.source}:${notification.id}`)
        ? { ...notification, readAt: now }
        : notification,
    );
    const stillUnread =
      target === 'all'
        ? new Set<string>()
        : new Set(
            [...unread].filter(
              (threadId) =>
                ('threadId' in target
                  ? threadId !== target.threadId
                  : !read.some((notification) => notification.threadId === threadId)) ||
                next.some(
                  (notification) =>
                    notification.threadId === threadId &&
                    notification.readAt === null &&
                    readable(notification, waitingTypes),
                ),
            ),
          );
    publish(next, stillUnread);
  };

  const markRead: NotificationFeed['markRead'] = (target) => {
    const read = list.filter(
      (notification) =>
        notification.readAt === null &&
        readable(notification, waitingTypes) &&
        (target === 'all' ||
          ('threadId' in target
            ? notification.threadId === target.threadId
            : target.some(
                (entry) => entry.id === notification.id && entry.source === notification.source,
              ))),
    );
    // Past the list, a conversation may still hold unread notifications only the server has.
    const beyond =
      target === 'all' ? unread.size > 0 : 'threadId' in target && unread.has(target.threadId);
    if (read.length === 0 && !beyond) return;
    showRead(read, target);
    const request: ReadNotificationsRequest =
      target === 'all'
        ? { all: true }
        : 'threadId' in target
          ? { threadId: target.threadId }
          : { ids: read.map((notification) => notification.id) };
    // Shown read at once; the next poll says what the servers kept.
    void markNotificationsRead(request).catch(() => undefined);
  };

  void poll();
  window.addEventListener('focus', onFocus);
  // Each event stored is streamed at once; the browser reconnects a dropped stream.
  const stream = typeof EventSource === 'undefined' ? null : new EventSource('/api/events/stream');
  if (stream !== null)
    stream.onmessage = (message) => {
      let event: FeedNotification;
      try {
        event = JSON.parse(String(message.data)) as FeedNotification;
      } catch {
        return;
      }
      for (const listener of [...streamedListeners]) listener(event);
      // Only what the user is told of changes the list.
      if (event.delivery !== 'off') void poll();
    };
  return {
    current: () => list,
    unreadThreadIds: () => unread,
    settle(target) {
      const settled = list.filter(
        (notification) =>
          notification.readAt === null &&
          target.some(
            (entry) => entry.id === notification.id && entry.source === notification.source,
          ),
      );
      if (settled.length > 0) showRead(settled, target);
    },
    setWaitingTypes(types) {
      waitingTypes = types;
    },
    failed: () => lastFailed,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    refresh,
    markRead,
    stop() {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      settle(waiting.splice(0));
      stream?.close();
      listeners.clear();
    },
  };
}
